// How long after showing the overlay before a *new* notification is allowed
// to trigger it again. A reasonable default pending tuning — chosen so the
// overlay reads as an occasional high-urgency interrupt rather than
// something that can fire repeatedly in a short burst (e.g. several
// transactional events landing back-to-back).
export const OVERLAY_FREQUENCY_CAP_WINDOW_MS = 15 * 60 * 1000 // 15 minutes

const LAST_SHOWN_AT_STORAGE_KEY = 'bstock.notificationOverlay.lastShownAt'
const ACTIVE_ID_STORAGE_KEY = 'bstock.notificationOverlay.activeId'

// Module-scope fallback for when sessionStorage throws (private browsing,
// blocked storage), mirroring sessionDismissal.ts's fallback so a storage
// failure degrades to "capped for the rest of this page load" instead of
// silently disabling the cap altogether.
let inMemoryLastShownAt: number | null = null
let inMemoryActiveId: string | null = null

const readStoredLastShownAt = (): number | null => {
  try {
    const raw = sessionStorage.getItem(LAST_SHOWN_AT_STORAGE_KEY)
    if (raw === null) return null
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : null
  } catch {
    // Private browsing, blocked storage, or corrupted contents — fall
    // through to whatever the in-memory fallback has.
    return null
  }
}

const readStoredActiveId = (): string | null => {
  try {
    return sessionStorage.getItem(ACTIVE_ID_STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * The id of the notification that was active as of the last
 * `recordOverlayShown` call, surviving a remount/reload within the same
 * session (unlike a plain `useRef`, which resets to `null` on remount) - the
 * policy hook reads this once on mount so an undismissed notice doesn't
 * disappear just because its host component happened to remount.
 *
 * Prefers the in-memory value over the stored one: `recordOverlayShown`
 * always updates the in-memory value, so it can only ever be *behind*
 * storage immediately after an actual page reload (when it starts at
 * `null`, and storage is the only surviving source) - never behind a
 * successful write. Checking storage first would let a stale stored id win
 * over a newer in-memory one whenever a `setItem` call happened to throw
 * (e.g. a transient quota error) after a prior write had already succeeded.
 */
export const getActiveNotificationId = (): string | null =>
  inMemoryActiveId ?? readStoredActiveId()

/**
 * Whether a *new* notification is allowed to trigger the overlay right now,
 * given when the overlay was last shown. Does not account for a
 * currently-active notification staying visible — callers that want a
 * single notification to remain shown across re-renders should track that
 * separately and only consult this for deciding whether to start showing a
 * different one.
 */
export const canShowOverlayNow = (nowMs: number = Date.now()): boolean => {
  // See getActiveNotificationId's doc comment for why in-memory is checked
  // first, not storage.
  const lastShownAt = inMemoryLastShownAt ?? readStoredLastShownAt()
  if (lastShownAt === null) return true
  return nowMs - lastShownAt >= OVERLAY_FREQUENCY_CAP_WINDOW_MS
}

/**
 * Marks "now" as the last time the overlay was shown, for cap purposes, and
 * records which notification is active - persisted alongside the timestamp
 * so `getActiveNotificationId()` can recover it after a remount/reload.
 */
export const recordOverlayShown = (
  id: string,
  nowMs: number = Date.now()
): void => {
  inMemoryLastShownAt = nowMs
  inMemoryActiveId = id
  try {
    sessionStorage.setItem(LAST_SHOWN_AT_STORAGE_KEY, String(nowMs))
    sessionStorage.setItem(ACTIVE_ID_STORAGE_KEY, id)
  } catch {
    // sessionStorage is unavailable — the in-memory fallback above already
    // recorded this for the rest of this page load.
  }
}

// Test-only: clears the module-scope in-memory fallback so each test
// observes its own cap state instead of whatever a prior test left behind.
export const __resetOverlayFrequencyCapForTests = (): void => {
  inMemoryLastShownAt = null
  inMemoryActiveId = null
}
